/**
 * Live spectrum analyzer, ported from the Rust player's `spectrum.rs`.
 *
 * Purely cosmetic: it runs over the output tap and never touches the stored waveform
 * analysis. The browser's own `AnalyserNode` smoothing is bypassed (`smoothingTimeConstant`
 * is set to 0 by the engine) so the envelope and peak-hold behaviour below is what
 * governs the look — otherwise two smoothing passes would fight each other.
 */

/** ~21 Hz resolution over ~46 ms at 44.1 kHz — lively without lagging. */
export const FFT_SIZE = 2_048;

/** Analyzer resolution. The strip downsamples these to whatever columns fit. */
export const BAR_COUNT = 192;

export const DB_FLOOR = -84;
export const DB_CEIL = -18;

/** Per-frame envelope at ~60fps: instant attack, eased release. */
const RELEASE = 0.88;

/** Peak caps snap up, hold, then fall under acceleration. */
const PEAK_HOLD_SECONDS = 0.65;
const CAP_GRAVITY = 1.8;

export type SpectrumDisplayMode = 'bars' | 'line';

export type SpectrumParams = {
  mode: SpectrumDisplayMode;
  dbFloor: number;
  dbCeil: number;
  /** Height multiplier applied after the dB map (0.25–3.0). */
  sensitivity: number;
  /** Lowest frequency in the centre, highs at both edges. */
  symmetric: boolean;
};

export const DEFAULT_SPECTRUM_PARAMS: SpectrumParams = {
  mode: 'bars',
  dbFloor: DB_FLOOR,
  dbCeil: DB_CEIL,
  sensitivity: 1,
  symmetric: true,
};

export function clampSpectrumParams(params: SpectrumParams): SpectrumParams {
  const floor = Math.min(Math.max(params.dbFloor, -120), -30);
  const ceil = Math.min(Math.max(params.dbCeil, floor + 6), 0);
  return {
    mode: params.mode,
    dbFloor: floor,
    dbCeil: ceil,
    sensitivity: Math.min(Math.max(params.sensitivity, 0.25), 3),
    symmetric: params.symmetric,
  };
}

/**
 * Log-spaced FFT bin ranges from ~30 Hz to ~18 kHz. Every bar gets at least one bin so
 * low-frequency bars never collapse to nothing.
 */
export function computeEdges(sampleRate: number, barCount = BAR_COUNT): [number, number][] {
  const binHz = sampleRate / FFT_SIZE;
  const maxBin = FFT_SIZE / 2;
  const fMin = 30;
  const fMax = Math.max(Math.min(sampleRate * 0.5, 18_000), fMin * 2);

  const edges: [number, number][] = [];
  let previous = Math.min(Math.max(Math.floor(fMin / binHz), 1), maxBin);
  for (let bar = 0; bar < barCount; bar += 1) {
    const fraction = (bar + 1) / barCount;
    const frequency = fMin * Math.pow(fMax / fMin, fraction);
    const end = Math.min(Math.max(Math.round(frequency / binHz), previous + 1), maxBin + 1);
    edges.push([previous, end]);
    previous = Math.min(end, maxBin);
  }
  return edges;
}

/**
 * Turns raw FFT magnitudes into display heights with attack/release and falling peak
 * caps. Holds no DOM or Web Audio references, so it can be driven directly in tests.
 */
export class SpectrumAnalyzer {
  readonly bars: Float32Array;
  readonly peaks: Float32Array;
  private readonly capVelocity: Float32Array;
  private readonly capHold: Float32Array;
  private edges: [number, number][];

  constructor(sampleRate: number, private readonly barCount = BAR_COUNT) {
    this.bars = new Float32Array(barCount);
    this.peaks = new Float32Array(barCount);
    this.capVelocity = new Float32Array(barCount);
    this.capHold = new Float32Array(barCount);
    this.edges = computeEdges(Math.max(sampleRate, 1), barCount);
  }

  setSampleRate(sampleRate: number): void {
    this.edges = computeEdges(Math.max(sampleRate, 1), this.barCount);
  }

  /**
   * `magnitudes` is a linear-magnitude spectrum (`getFloatFrequencyData` is already in
   * dB, so the engine converts; this takes linear values to match `spectrum.rs`).
   */
  update(magnitudes: Float32Array, params: SpectrumParams, dt: number): void {
    const range = Math.max(params.dbCeil - params.dbFloor, 1);
    const sensitivity = Math.min(Math.max(params.sensitivity, 0.25), 3);

    for (let bar = 0; bar < this.barCount; bar += 1) {
      const [start, rawEnd] = this.edges[bar]!;
      const end = Math.min(rawEnd, magnitudes.length);
      let magnitude = 0;
      for (let bin = start; bin < end; bin += 1) {
        if (magnitudes[bin]! > magnitude) magnitude = magnitudes[bin]!;
      }
      const decibels = 20 * Math.log10(magnitude + 1e-9);
      const target = Math.min(
        Math.max(((decibels - params.dbFloor) / range) * sensitivity, 0),
        1,
      );
      this.bars[bar] = target >= this.bars[bar]! ? target : this.bars[bar]! * RELEASE;
      this.updateCap(bar, dt);
    }
  }

  /** Ease everything down when nothing is playing — no FFT needed. */
  decay(dt: number): void {
    for (let bar = 0; bar < this.barCount; bar += 1) {
      this.bars[bar] = this.bars[bar]! * RELEASE;
      this.updateCap(bar, dt);
    }
  }

  private updateCap(bar: number, dt: number): void {
    if (this.bars[bar]! >= this.peaks[bar]!) {
      this.peaks[bar] = this.bars[bar]!;
      this.capVelocity[bar] = 0;
      this.capHold[bar] = PEAK_HOLD_SECONDS;
    } else if (this.capHold[bar]! > 0) {
      this.capHold[bar] = this.capHold[bar]! - dt;
    } else {
      this.capVelocity[bar] = this.capVelocity[bar]! + CAP_GRAVITY * dt;
      this.peaks[bar] = Math.max(this.peaks[bar]! - this.capVelocity[bar]! * dt, this.bars[bar]!);
    }
  }
}


/**
 * Lay out evenly spaced columns across `width`.
 *
 * Columns are added as the strip widens, up to `maxColumns` — for the analyser that
 * ceiling is the number of frequency bands it actually resolves, and past it the bars
 * widen to fill instead. Without that second step a wide window draws its bars at a
 * fixed pitch from the left edge and leaves the rest of the strip empty.
 */
export function columnLayout(
  width: number,
  minPitch: number,
  maxColumns: number,
): { columns: number; slot: number; barWidth: number } {
  if (width <= 0 || maxColumns <= 0) return { columns: 0, slot: 0, barWidth: 0 };
  const columns = Math.max(1, Math.min(Math.floor(width / minPitch), maxColumns));
  const slot = width / columns;
  return { columns, slot, barWidth: Math.max(1, slot - 1) };
}

/** Loudest value per display bucket — keeps transients visible when downsampling. */
export function bucketMax(values: Float32Array, bucket: number, buckets: number): number {
  const start = Math.floor((bucket * values.length) / buckets);
  const end = Math.min(Math.max(Math.floor(((bucket + 1) * values.length) / buckets), start + 1), values.length);
  let maximum = 0;
  for (let index = start; index < end; index += 1) {
    if (values[index]! > maximum) maximum = values[index]!;
  }
  return maximum;
}

/**
 * Reorder bars so the lowest frequency sits in the centre and highs run out to both
 * edges — the `symmetric` option from `SpectrumParams`.
 */
export function toDisplayColumns(
  values: Float32Array,
  columns: number,
  symmetric: boolean,
): Float32Array {
  const output = new Float32Array(columns);
  if (!symmetric) {
    for (let column = 0; column < columns; column += 1) {
      output[column] = bucketMax(values, column, columns);
    }
    return output;
  }
  const half = Math.ceil(columns / 2);
  for (let index = 0; index < half; index += 1) {
    const value = bucketMax(values, index, half);
    const right = Math.floor(columns / 2) + index;
    const left = Math.floor(columns / 2) - 1 - index;
    if (right < columns) output[right] = value;
    if (left >= 0) output[left] = value;
  }
  return output;
}
